import {
  type AdminManagedEmailView,
  ApiError,
  adminManagedEmailAction,
  adminManagedEmailGet,
} from '@cortex/web-api/clients/admin/api';
import { useEffect, useRef, useState } from 'react';
const labels = {
  directory_pending: 'Mailbox provisioning needs attention',
  stage_pending: 'Accounting email setup pending',
  delivery_needed: 'Delivery test needed',
  delivery_pending: 'Waiting for delivery confirmation',
  activation_pending: 'Delivery verified — accounting activation pending',
  active_preserved: 'Existing accounting email connection preserved',
  blocked: 'Email setup needs attention',
  outcome_unknown: 'Previous operation needs reconciliation',
  preserved: 'Existing email setup needs a check',
};
export function ManagedEmailOnboardingPanel({
  slug,
  onSessionExpired,
}: { slug: string; onSessionExpired: () => void }) {
  const [view, setView] = useState<AdminManagedEmailView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const epoch = useRef(0);
  // biome-ignore lint/correctness/useExhaustiveDependencies: refresh explicitly requests another persisted read.
  useEffect(() => {
    const generation = ++epoch.current;
    const controller = new AbortController();
    setView(null);
    setError(null);
    setBusy(false);
    void adminManagedEmailGet(slug, controller.signal)
      .then((result) => {
        if (epoch.current === generation) setView(result);
      })
      .catch((e) => {
        if (controller.signal.aborted || epoch.current !== generation) return;
        if (e instanceof ApiError && e.status === 401) onSessionExpired();
        else setError('Could not load accounting email setup.');
      });
    return () => {
      controller.abort();
      ++epoch.current;
    };
  }, [slug, refresh, onSessionExpired]);
  async function act(action: 'continue' | 'delivery-test') {
    if (!view || busy) return;
    const generation = epoch.current;
    setBusy(true);
    setError(null);
    try {
      const next = await adminManagedEmailAction(slug, view.revision, action);
      if (epoch.current === generation) setView(next);
    } catch (e) {
      if (epoch.current !== generation) return;
      if (e instanceof ApiError && e.status === 401) onSessionExpired();
      else
        setError(
          e instanceof ApiError && e.status === 409
            ? 'Setup changed. Refresh its status before trying again.'
            : 'Could not confirm the request. Refresh its status before trying again.',
        );
    } finally {
      if (epoch.current === generation) setBusy(false);
    }
  }
  return (
    <section
      className="card"
      aria-label="Accounting email setup"
      style={{ padding: 'var(--space-6)' }}
    >
      <h2 className="h3">Accounting email setup</h2>
      {error && <p role="alert">{error}</p>}
      {!view && !error && <p>Loading email setup…</p>}
      {view && (
        <>
          <p>
            {view.enrolled
              ? view.initialSetup === 'waiting_for_mailbox'
                ? 'Waiting for the new mailbox and aliases to become visible. Setup will check again automatically.'
                : view.initialSetup === 'waiting_for_infrastructure'
                  ? 'Mailbox is ready. Client infrastructure setup is queued.'
                  : view.initialSetup === 'infrastructure_started'
                    ? 'Client infrastructure setup has started. An operator must reconcile an interrupted run before retrying.'
                    : labels[view.phase]
              : 'Managed email has not been checked for this client.'}
          </p>
          {view.enrolled && (
            <>
              <dl>
                <dt>Mailbox</dt>
                <dd>{view.primaryAddress}</dd>
                <dt>Invoices</dt>
                <dd>{view.invoiceRecipient}</dd>
                <dt>E-transfers</dt>
                <dd>{view.etransferRecipient}</dd>
              </dl>
              <p>Managed by Acel Cortex</p>
              {view.errorCode === 'verification_email_provider_required' && (
                <p>Configure the tenant’s email sending provider, then send the delivery test.</p>
              )}
              {view.errorCode === 'delivery_test_rate_limited' && (
                <p>
                  A delivery test was started recently. Check its delivery or wait a minute before
                  explicitly replacing it.
                </p>
              )}
              {view.errorCode === 'delivery_test_revision_changed' && (
                <p>The delivery test changed. Check again before sending another test.</p>
              )}
              <p>
                Secure first-login completion is not recorded here. Use Google Admin to arrange the
                customer’s first login securely.
              </p>
              <p>Workspace license assignment confirmation is not recorded here.</p>
              <p>
                Last check:{' '}
                {view.lastCheckedAt
                  ? new Date(view.lastCheckedAt).toLocaleString()
                  : 'Not checked yet'}
              </p>
              {view.pending && <output>An email setup check is queued or running.</output>}
              {view.sendUnresolved && (
                <p>
                  The previous send response is unknown. Check again to inspect its delivery, or
                  explicitly send a replacement test. An older request cannot replace the new test.
                </p>
              )}
            </>
          )}
          {!view.infrastructureReady && (
            <p>Finish client infrastructure setup before checking its email.</p>
          )}
          <button
            type="button"
            className="btn-secondary"
            disabled={busy || !view.infrastructureReady}
            onClick={() => void act('continue')}
          >
            Check again
          </button>
          {view.enrolled && view.canSendTest && (
            <div>
              <p>Sends a new delivery test to both accounting aliases.</p>
              <button
                type="button"
                className="btn-secondary"
                disabled={busy}
                onClick={() => void act('delivery-test')}
              >
                Send delivery test
              </button>
            </div>
          )}
        </>
      )}
      <button
        type="button"
        className="btn-secondary"
        disabled={busy}
        onClick={() => setRefresh((n) => n + 1)}
      >
        Refresh status
      </button>
    </section>
  );
}
